import React from 'react';

import { ContentCopy } from '@mui/icons-material';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { Grid, IconButton, Stack } from '@mui/material';
import { GridActionsCellItem } from '@mui/x-data-grid';
import { PromoCode } from 'common/contracts';
import { DATE_TIME_FORMAT } from 'constants/constants';
import dayjs from 'dayjs';
import { StatusLabel } from 'design-system/StatusLabel/StatusLabel';
import { EStatusLabel } from 'design-system/StatusLabel/types';
import { EToasterStatus, showToast } from 'utils/showToast';
import { STATUS_TO_ICON_MAP } from 'views/Coupons/utils';

import AcCard from 'components/AcCard/AcCard';
import AcInput from 'components/AcInput/AcInput';
import ActionButton from 'components/ActionButton/ActionButton';
import DataTable from 'components/DataTable/DataTable';

import { CouponFormValues } from '../../types';

interface CouponsPromoCodesProps {
  couponFormValues: CouponFormValues;
  handleBlur: (e: React.FocusEvent<HTMLInputElement>) => void;
  setFieldValue: (field: string, value: any) => void;
  promoCode: string;
  setPromoCode: (value: string) => void;
  maxRedemptions: string | number;
  setMaxRedemptions: (value: number) => void;
  isPromoCodeActive: any;
}

export default function CouponsPromoCodes({
  couponFormValues,
  handleBlur,
  setFieldValue,
  promoCode,
  setPromoCode,
  maxRedemptions,
  setMaxRedemptions,
  isPromoCodeActive
}: CouponsPromoCodesProps) {
  const onAddPromoCode = () => {
    const trimmedPromoCode = promoCode.trimEnd();
    if (!trimmedPromoCode || +maxRedemptions === 0) return;

    // Check if promo code name already exists in the current list
    const isDuplicate = couponFormValues.promoCodes.some(
      (existingCode) =>
        existingCode.name.toLowerCase() === trimmedPromoCode.toLowerCase()
    );

    if (isDuplicate) {
      showToast({
        message: `Promo code "${trimmedPromoCode}" already exists in this coupon`,
        status: EToasterStatus.ERROR
      });
      return;
    }

    isPromoCodeActive.mutate(trimmedPromoCode, {
      onSuccess: (isPromoCodeActiveResponse: {
        isPromoCodeActive: boolean;
        couponName?: string;
      }) => {
        const { isPromoCodeActive, couponName } = isPromoCodeActiveResponse;
        if (isPromoCodeActive === false) {
          const newPromoCodes = [
            {
              active: true,
              name: trimmedPromoCode,
              maxRedemptions: maxRedemptions,
              promoCodeAvailability: [
                {
                  redemptions: 0,
                  maxRedemptions: maxRedemptions
                }
              ],
              createdAt: new Date().toISOString(),
              fresh: true
            },
            ...couponFormValues.promoCodes
          ];
          setFieldValue('promoCodes', newPromoCodes);
          setPromoCode('');
          setMaxRedemptions(0);
        } else {
          showToast({
            message: `${promoCode} is currently active on coupon: ${couponName}`,
            status: EToasterStatus.ERROR
          });
        }
      },
      onError: () => {
        showToast({
          message: `Failed to add promo code: ${trimmedPromoCode} `,
          status: EToasterStatus.ERROR
        });
      }
    });
  };

  return (
    <AcCard
      stackContainer={false}
      title="Promo Codes"
      customStyles={{
        border: '1px solid #cacbd4',
        borderRadius: '4px',
        padding: '1em',
        marginTop: '1em'
      }}
    >
      <Grid container rowSpacing={2} columnSpacing={1.5}>
        <Grid item xs={4}>
          <AcInput
            header="Promo code name"
            name=""
            value={promoCode}
            onChange={(e) => setPromoCode(e.target.value)}
            onBlur={handleBlur}
          />
        </Grid>
        <Grid item xs={4}>
          <AcInput
            header="Total promo code redemptions"
            name=""
            value={maxRedemptions}
            onChange={(e) => setMaxRedemptions(Number(e.target.value))}
            onBlur={handleBlur}
            type="number"
          />
        </Grid>
        <Grid item xs={4} display="flex" alignItems="flex-end">
          <ActionButton
            variant="outlined"
            text="Add New"
            isLoading={isPromoCodeActive.isLoading}
            disabled={
              !promoCode || +maxRedemptions === 0 || isPromoCodeActive.isLoading
            }
            style={{
              height: '48px',
              width: '115px'
            }}
            onClick={onAddPromoCode}
          />
        </Grid>
        <Grid item xs={12}>
          <DataTable
            columns={[
              {
                headerName: 'Name',
                field: 'name',
                flex: 1,
                renderCell: (params) => {
                  return (
                    <Stack direction="row" alignItems="center">
                      <span>{params.row.name}</span>
                      <IconButton
                        onClick={() => {
                          navigator.clipboard.writeText(params.row.name);
                          showToast({
                            message: `Promo code copied`,
                            status: EToasterStatus.SUCCESS
                          });
                        }}
                      >
                        <ContentCopy />
                      </IconButton>
                    </Stack>
                  );
                }
              },
              {
                headerName: 'Redemptions',
                field: 'promoCodeAvailability',
                flex: 1,
                renderCell: ({ value, row }) => {
                  const availability = value[0];
                  if (!availability) {
                    return '';
                  }
                  const maxRedemptions =
                    availability.maxRedemptions || row.maxRedemptions;
                  const redemptions = availability.redemptions;
                  return (
                    <span>
                      {redemptions} / {maxRedemptions}
                    </span>
                  );
                }
              },
              {
                headerName: 'Creation Date',
                field: 'createdAt',
                flex: 1,
                renderCell: (params) => {
                  return params.value ? (
                    <span>
                      {dayjs(params.value).utc().format(DATE_TIME_FORMAT)}
                    </span>
                  ) : (
                    'N/A'
                  );
                }
              },
              {
                headerName: 'Status',
                field: 'active',
                flex: 1,
                renderCell: (params) => {
                  if (params.row.fresh || params.row.isStatusChanging) {
                    return (
                      <StatusLabel
                        prefixIcon={STATUS_TO_ICON_MAP[EStatusLabel.PENDING]}
                        text={'Pending'}
                        status={EStatusLabel.PENDING}
                      />
                    );
                  }
                  const status = !!params.value
                    ? EStatusLabel.SUCCESS
                    : EStatusLabel.FAILED;
                  return (
                    <StatusLabel
                      prefixIcon={STATUS_TO_ICON_MAP[status]}
                      text={!!params.value ? 'Active' : 'Inactive'}
                      status={status}
                    />
                  );
                }
              },
              {
                field: 'actions',
                flex: 1,
                type: 'actions',
                width: 80,
                disableReorder: true,
                getActions: (params: any) => {
                  if (params.row.fresh) {
                    return [
                      <GridActionsCellItem
                        icon={<CancelIcon />}
                        label={'Cancel'}
                        onClick={() => {
                          const newPromoCodes =
                            couponFormValues.promoCodes.filter(
                              (promoCode: any) =>
                                promoCode.name !== params.row.name
                            );
                          setFieldValue('promoCodes', newPromoCodes);
                        }}
                        showInMenu
                      />
                    ];
                  }
                  return [
                    <GridActionsCellItem
                      icon={<CheckCircleOutlineIcon />}
                      label={params.row.active ? 'Deactivate' : 'Activate'}
                      onClick={() => {
                        const newPromoCodes = couponFormValues.promoCodes.map(
                          (promoCode: any) => {
                            if (promoCode.name === params.row.name) {
                              const newActiveState = !promoCode.active;
                              const isStatusChanging =
                                newActiveState !==
                                promoCode.originalActiveState;
                              return {
                                ...promoCode,
                                active: newActiveState,
                                isStatusChanging,
                                originalActiveState:
                                  promoCode.originalActiveState ??
                                  promoCode.active
                              };
                            }
                            return promoCode;
                          }
                        );
                        setFieldValue('promoCodes', newPromoCodes);
                      }}
                      showInMenu
                    />
                  ];
                }
              }
            ]}
            rowIdIdentifier="name"
            rows={couponFormValues.promoCodes || []}
            loading={false}
          />
        </Grid>
      </Grid>
    </AcCard>
  );
}
